import React, { useCallback, useEffect, useState } from "react";
import { Plus, X, Trash2, BookOpen } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { PageHead, Field, inputCls, SERVICE_CATEGORIES, label } from "./crmUi";

const FIELDS = [
  ["sales_instructions", "Sales instructions"],
  ["questions_to_ask", "Questions to ask"],
  ["photo_measurement_requirements", "Photo / measurement requirements"],
  ["site_visit_rules", "Site visit rules"],
  ["pricing_guidance_rules", "Pricing guidance rules"],
  ["escalation_rules", "Escalation rules"],
  ["followup_templates", "Follow-up templates"],
  ["objection_handling_notes", "Objection handling notes"],
  ["upsell_suggestions", "Upsell suggestions"],
];

export default function KnowledgePage() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    crmApi.knowledge().then((d) => setItems(d.knowledge)).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { id, updated_at, ...body } = editing;
      if (id) await crmApi.updateKnowledge(id, body);
      else await crmApi.createKnowledge(body);
      setEditing(null);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete knowledge for ${label(SERVICE_CATEGORIES, k.service_category)}?`)) return;
    try { await crmApi.deleteKnowledge(k.id); reload(); } catch (e) { setErr(e.message); }
  };

  return (
    <div data-testid="crm-knowledge-page">
      <PageHead eyebrow="AI Training" title="AI Knowledge Base">
        <button
          type="button"
          onClick={() => setEditing({ service_category: "" })}
          data-testid="crm-knowledge-new"
          className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F]"
        >
          <Plus size={12} /> Add Service Playbook
        </button>
      </PageHead>
      <p className="text-[12px] text-[#7A6B5F] mb-5 max-w-2xl">
        These playbooks guide the AI when scoring leads, summarising conversations and drafting replies —
        and later the WhatsApp auto-reply bot. One entry per service category.
      </p>
      {err && !editing && <p className="text-xs text-[#A3141A] mb-2">{err}</p>}
      {!items && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
      {items && items.length === 0 && <p className="text-sm text-[#7A6B5F] italic">No playbooks yet.</p>}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((k) => (
            <div key={k.id} data-testid={`crm-knowledge-card-${k.id}`} className="bg-white border border-[#6E1F2C]/10 p-5">
              <div className="flex items-center justify-between">
                <p className="font-serif-display text-lg text-[#1C110C] inline-flex items-center gap-2">
                  <BookOpen size={15} className="text-[#A6822F]" /> {label(SERVICE_CATEGORIES, k.service_category)}
                </p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditing({ ...k })} data-testid={`crm-knowledge-edit-${k.id}`} className="text-[9px] tracking-[0.16em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C] px-3 py-1.5 hover:bg-[#6E1F2C] hover:text-white">Edit</button>
                  <button type="button" onClick={() => remove(k)} className="text-[9px] uppercase border border-[#A3141A]/40 text-[#A3141A] px-2 py-1.5 hover:bg-[#A3141A] hover:text-white"><Trash2 size={11} /></button>
                </div>
              </div>
              <p className="text-[12px] text-[#3E2A22] mt-2 line-clamp-3">{k.sales_instructions || "No instructions yet."}</p>
              <p className="text-[10px] text-[#7A6B5F] mt-2">
                {FIELDS.filter(([f]) => k[f]).length}/{FIELDS.length} sections filled
              </p>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#1C110C]/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-[680px] border border-[#6E1F2C]/20 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-knowledge-modal">
            <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-serif-display text-lg text-[#1C110C]">
                {editing.id ? `Edit — ${label(SERVICE_CATEGORIES, editing.service_category)}` : "New Service Playbook"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="text-[#7A6B5F]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3">
              {!editing.id && (
                <Field label="Service category">
                  <select data-testid="crm-knowledge-category" className={inputCls} value={editing.service_category} onChange={(e) => setEditing({ ...editing, service_category: e.target.value })}>
                    <option value="">— Select —</option>
                    {SERVICE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
              )}
              {FIELDS.map(([f, l]) => (
                <Field key={f} label={l}>
                  <textarea
                    rows={2}
                    data-testid={`crm-knowledge-${f}`}
                    className={inputCls}
                    value={editing[f] || ""}
                    onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                  />
                </Field>
              ))}
              {err && <p className="text-xs text-[#A3141A]">{err}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">Cancel</button>
                <button type="button" onClick={save} disabled={busy || !editing.service_category} data-testid="crm-knowledge-save" className="bg-[#6E1F2C] text-white px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-60">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
